import React, { useEffect, useRef, useState } from 'react';
import {
  chatOnboardingCollector,
  startOnboarding,
  getBillingPlans,
  previewOnboardingStrategy,
  previewOnboardingPosts,
} from '../api';
import type { OnboardingPreview, OnboardingPreviewPost } from '../api';
import type { SubscriptionPlan, User, Workspace } from '../types';
import ConistraMark from '../components/ConistraMark';
import { track, identifyUser } from '../analytics';
import { initMetaPixel, metaTrack, planValue, planCurrency } from '../metaPixel';
import './OnboardingCollectorPage.css';

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

type CollectedContext = {
  niche: string;
  tone?: string;
  goal?: string;
};

type AccountFormData = {
  first_name: string;
  email: string;
  password: string;
};

/**
 * Signup, one question at a time.
 *
 * The old form asked for email, password and an Instagram handle on one card
 * and looked like a form, which is exactly what someone who has just been shown
 * their own page does not want to meet next. Three questions, one per screen,
 * each answerable in a couple of seconds.
 *
 * Name goes first even though the backend barely needs it: it is the easiest
 * question anyone can answer, it costs nothing to get wrong, and answering it
 * makes answering the next one feel like continuing rather than starting. Every
 * screen after it can then use their name.
 *
 * The Instagram handle is gone from here entirely. It is the one field that can
 * stall someone — they have to decide the account, maybe go and make it — and
 * nothing before payment needs it. Settings already collects it.
 */
const SIGNUP_STEPS = [
  {
    key: 'first_name' as const,
    label: 'first name',
    type: 'text',
    autoComplete: 'given-name',
    placeholder: 'Alex',
  },
  {
    key: 'email' as const,
    label: 'email',
    type: 'email',
    autoComplete: 'email',
    placeholder: 'you@example.com',
  },
  {
    key: 'password' as const,
    label: 'password',
    type: 'password',
    autoComplete: 'new-password',
    placeholder: 'At least 8 characters',
  },
];

/**
 * chat → building → reveal → account
 *
 * `building` and `reveal` are the whole point of this screen: the visitor used
 * to go from "what's your account about?" straight to a password and a credit
 * card, having been shown nothing. Now the product does its job first, in front
 * of them, and only then asks.
 */
type Stage = 'chat' | 'building' | 'reveal' | 'account';

/**
 * The build steps, in order. These are real: each one maps to work that is
 * actually happening, and it flips to done when that work returns.
 *
 * Named steps rather than a progress bar on purpose — visible, specific labour
 * is what makes a wait feel like value being made instead of time being taken
 * (the "labour illusion"). It only works while it stays honest: a bar that
 * animates to 90% and parks there does the opposite.
 */
const BUILD_STEPS = [
  { key: 'brief', label: 'Reading your idea' },
  { key: 'strategy', label: 'Writing your strategy' },
  { key: 'posts', label: 'Designing your first posts' },
] as const;

/** Stagger between post cards on the reveal. */
const POST_REVEAL_MS = 420;

/**
 * A stable per-post variation of the brand gradient, derived from its own text.
 *
 * The range is deliberately narrow. The signal gradient runs pink → coral →
 * orange, and orange sits close enough to yellow that even a moderate rotation
 * walks the third stop into olive — off-brand and, frankly, ugly. ±14° is
 * enough for the cards to read as distinct without any of them leaving the
 * family; the small saturation shift does the rest of the work.
 */
function fieldStyleFor(text: string): React.CSSProperties {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 100000;
  }
  const hue = (hash % 5) * 7 - 14; // -14°…+14°
  const sat = 0.92 + ((hash >> 3) % 5) * 0.045; // 0.92…1.10
  return { filter: `hue-rotate(${hue}deg) saturate(${sat.toFixed(2)})` };
}

type Props = {
  planCode: string;
  onComplete: (_user: User, _workspace: Workspace | null) => void;
  onBack: () => void;
};

/** Duration of the account card slide-up animation (matches CSS .oc-anim-slide-up). */
const ACCOUNT_ANIM_MS = 350;

const QUICK_REPLIES = [
  'gym motivation 💪',
  'travel photography 📸',
  'vegan cooking 🌱',
  'personal finance 💰',
  'fashion & style 👗',
  'tech & gadgets 🔧',
];

const FIRST_ASSISTANT_QUESTION = "Hey! 👋 What's your Instagram account going to be about?";

const ConistraLogo = ({ size = 28 }: { size?: number }) => <ConistraMark size={size} />;

/**
 * Slots on the preview profile. Nine, not twelve: three filled out of nine
 * reads as a third of the way there, where three of twelve reads as mostly
 * empty — and a fourth row of dark squares turns the frame into a tall void
 * that has to be scrolled past to reach the button.
 */
const GRID_SLOTS = 9;

/**
 * One post as a tile on the profile grid.
 *
 * Square, because that is what an Instagram grid is. The tile carries the hook
 * over the brand gradient rather than a faked photograph: nothing here has been
 * rendered yet, and pretending otherwise would be a promise checkout could not
 * keep.
 *
 * On a device with a pointer, hovering "develops" the tile — the grain clears,
 * the colour deepens — and a Generate pill appears. That shortcut does not
 * exist on touch, where hovering is not a thing, so the same action lives on
 * the detail panel a tap opens. Both land in the same place.
 */
function GridTile({
  post,
  index,
  active,
  onOpen,
  onGenerate,
}: {
  post: OnboardingPreviewPost;
  index: number;
  active: boolean;
  onOpen: () => void;
  onGenerate: () => void;
}) {
  return (
    <div
      className={`oc-tile oc-tile--filled${active ? ' is-active' : ''}`}
      style={{ ...fieldStyleFor(post.title), animationDelay: `${index * 220}ms` }}
    >
      <button type="button" className="oc-tile__open" onClick={onOpen}>
        <span className="oc-tile__grain" aria-hidden="true" />
        <span className="oc-tile__hook">{post.title}</span>
        <span className="oc-tile__sr">Open post {index + 1}</span>
      </button>
      <button
        type="button"
        className="oc-tile__gen"
        onClick={onGenerate}
        tabIndex={-1}
        aria-hidden="true"
      >
        Generate →
      </button>
    </div>
  );
}

function OnboardingCollectorPage({ planCode, onComplete, onBack }: Props) {
  const [stage, setStage] = useState<Stage>('chat');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  // Chips the agent generated for the question it just asked. The visitor types
  // only their first message; after that they choose, which keeps the exchange
  // real without spending the seconds we don't have on thumb-typing.
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  // Set at handoff. Its presence is what turns the chat's last message into a
  // step the visitor takes rather than one that happens to them.
  const [handoffLabel, setHandoffLabel] = useState('');
  const [initDone, setInitDone] = useState(false);

  // Context collected by the chat agent
  const [context, setContext] = useState<CollectedContext | null>(null);

  // The pre-payment preview: the strategy and posts the visitor sees for free.
  const [preview, setPreview] = useState<OnboardingPreview | null>(null);
  const [buildStep, setBuildStep] = useState(0);
  const [revealedPosts, setRevealedPosts] = useState(0);
  const [previewError, setPreviewError] = useState('');
  const [openPost, setOpenPost] = useState<number | null>(null);
  // Held across the whole flow and handed to startOnboarding — without it every
  // completion above is orphaned and the paid account starts empty.
  const previewTokenRef = useRef<string>('');

  // Account form
  const [accountForm, setAccountForm] = useState<AccountFormData>({
    first_name: '',
    email: '',
    password: '',
  });
  const [signupStep, setSignupStep] = useState(0);
  const [accountErrors, setAccountErrors] = useState<Partial<AccountFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<ChatMessage[]>([]);
  historyRef.current = messages;

  useEffect(() => {
    getBillingPlans()
      .then((plans) => setPlan(plans.find((p) => p.code === planCode) ?? null))
      .catch(() => {});
  }, [planCode]);

  // Funnel step 1 of 4 — the landing-page CTA landed. Fires once per mount.
  useEffect(() => {
    track('app_signup_started', { plan_code: planCode });
    // Boot the ad pixel here, at the top of the funnel, rather than letting the
    // first conversion boot it: fbevents.js then has the whole preview build to
    // load, so the event that matters is never racing a cold network fetch.
    initMetaPixel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kick off agent greeting on mount
  useEffect(() => {
    if (initDone) return;
    setInitDone(true);
    runGreetingAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus email field when switching to account stage
  useEffect(() => {
    if (stage === 'account') {
      setTimeout(() => emailRef.current?.focus(), ACCOUNT_ANIM_MS);
    }
  }, [stage]);

  const runGreetingAnimation = () => {
    setLoading(true);
    window.setTimeout(() => {
      setMessages([{ role: 'assistant', content: FIRST_ASSISTANT_QUESTION }]);
      setShowQuickReplies(true);
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }, 550);
  };

  /**
   * Build the account in front of the visitor.
   *
   * Two calls, deliberately: the strategy lands and is shown while the posts
   * are still being written, so there is real output on screen during the wait
   * rather than a spinner. Both are replayable — the server stores each result
   * against the session token, so a retry after a failure never bills twice.
   */
  const runPreview = async (ctx: CollectedContext) => {
    setPreviewError('');
    setBuildStep(1);
    try {
      const withStrategy = await previewOnboardingStrategy({
        niche: ctx.niche,
        tone: ctx.tone,
        goal: ctx.goal,
        token: previewTokenRef.current || undefined,
      });
      previewTokenRef.current = withStrategy.token;
      setPreview(withStrategy);
      setBuildStep(2);
      track('app_onboarding_strategy_shown', { niche_length: ctx.niche.length });

      const withPosts = await previewOnboardingPosts({ token: withStrategy.token });
      setPreview(withPosts);
      setBuildStep(BUILD_STEPS.length);
      track('app_onboarding_posts_shown', { post_count: withPosts.posts.length });
      // The aha moment, and by far the highest-volume in-app signal we have. At
      // current traffic it is the only event with any chance of reaching the
      // volume Meta needs to optimise against, so it is worth reporting even
      // though nobody has committed to anything yet.
      metaTrack('ViewContent', {
        content_name: 'onboarding_preview',
        content_category: 'signup_funnel',
        num_items: withPosts.posts.length,
      });

      setStage('reveal');
    } catch (err) {
      // Loud, and recoverable. The one thing this must never do is leave them
      // on a screen with nothing happening and no way forward.
      setPreviewError(
        err instanceof Error ? err.message : 'Something went wrong building your page.'
      );
    }
  };

  const retryPreview = () => {
    if (context) runPreview(context);
  };

  /**
   * Every route to checkout, from wherever it was triggered.
   *
   * One destination, several doors: the dock button, a tile's Generate pill, and
   * the detail panel. A CTA attached to a post they just read is concrete where
   * the collective one is abstract, and `from` records which door actually works.
   */
  const goToAccount = (from: 'dock' | 'tile' | 'detail') => {
    track('app_paywall_reached', { from, post_count: preview?.posts.length ?? 0 });
    metaTrack('AddToCart', {
      content_name: planCode,
      content_category: 'signup_funnel',
      value: planValue(plan?.price_cents),
      currency: planCurrency(plan?.currency),
    });
    track('app_signup_step_viewed', { step: SIGNUP_STEPS[0].key, index: 0 });
    setStage('account');
  };

  // Reveal the post cards one at a time. Three arriving together is a list;
  // three arriving in sequence is a performance — and it gives the eye time to
  // actually read each one.
  useEffect(() => {
    if (stage !== 'reveal' || !preview) return;
    const total = preview.posts.length;
    if (revealedPosts >= total) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setRevealedPosts(total);
      return;
    }
    const timer = window.setTimeout(
      () => setRevealedPosts((n) => n + 1),
      revealedPosts === 0 ? 220 : POST_REVEAL_MS
    );
    return () => window.clearTimeout(timer);
  }, [stage, preview, revealedPosts]);

  const sendTurn = async (text: string, via: 'typed' | 'chip' = 'typed') => {
    const history = historyRef.current;
    // One event per turn. Without it a visitor who leaves mid-conversation is
    // indistinguishable from one who never started — the exact blind spot that
    // hid the old funnel's collapse for eighteen days.
    track('app_onboarding_chat_turn', {
      turn: Math.floor(history.length / 2) + 1,
      via,
    });
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setShowQuickReplies(false);
    setQuickReplies([]);
    setHandoffLabel('');
    setLoading(true);

    try {
      const historyWithOpener =
        history.length === 0
          ? [{ role: 'assistant' as const, content: FIRST_ASSISTANT_QUESTION }]
          : history;

      const result = await chatOnboardingCollector({
        history: historyWithOpener.map((m) => ({ role: m.role, content: m.content })),
        message: text,
      });

      setMessages((prev) => [...prev, { role: 'assistant', content: result.message || '…' }]);
      setQuickReplies(result.quick_replies ?? []);

      if (result.ready_for_account && result.data) {
        const ctx = result.data;
        setContext(ctx);
        // No timer. The agent has just told them what it is about to build, and
        // a screen that swaps itself out mid-sentence reads as something being
        // done to them. They press the button, so the build is their decision —
        // and the label is the agent's own words, already in their language.
        setHandoffLabel(result.cta_label || 'Build my page');
        // Reaching the handoff and taking it are different questions. Against
        // app_onboarding_strategy_shown this gives the press rate on the one
        // button that starts the whole build.
        track('app_onboarding_handoff_shown', {
          turns: Math.floor(historyRef.current.length / 2) + 1,
        });
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Something went wrong on my end — give it a second and try again 🙏',
        },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    sendTurn(text);
  };

  const handleQuickReply = (text: string) => {
    if (loading) return;
    sendTurn(text, 'chip');
  };

  // Account form submission
  /** Validate only the step in front of them — never a list of what else is wrong. */
  const stepError = (index: number): string | null => {
    const step = SIGNUP_STEPS[index];
    const value = accountForm[step.key].trim();
    if (step.key === 'first_name') {
      return value ? null : 'Just a first name is fine.';
    }
    if (step.key === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? null
        : 'That does not look like an email address.';
    }
    return accountForm.password.length >= 8 ? null : 'At least 8 characters.';
  };

  const advance = () => {
    const err = stepError(signupStep);
    if (err) {
      setAccountErrors((p) => ({ ...p, [SIGNUP_STEPS[signupStep].key]: err }));
      return;
    }
    if (signupStep < SIGNUP_STEPS.length - 1) {
      const next = signupStep + 1;
      setSignupStep(next);
      // Three questions is three places to lose someone. Shipping a new form
      // without knowing which one costs us would repeat the whole problem.
      track('app_signup_step_viewed', { step: SIGNUP_STEPS[next].key, index: next });
      return;
    }
    submitAccount();
  };

  const submitAccount = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const origin = window.location.origin;
      const result = await startOnboarding({
        email: accountForm.email.trim(),
        password: accountForm.password,
        first_name: accountForm.first_name.trim(),
        niche: context?.niche ?? '',
        tone: context?.tone,
        goal: context?.goal,
        plan_code: planCode,
        success_url: `${origin}/activate`,
        cancel_url: `${origin}/`,
        onboarding_token: previewTokenRef.current || undefined,
      });

      // Funnel step 2 of 4 — the account now exists. Identify first so this
      // event and every anonymous marketing pageview before it collapse onto
      // one person.
      identifyUser(result.user);
      track('app_signup_completed', {
        plan_code: planCode,
        paid_plan: Boolean(plan && plan.price_cents > 0),
      });
      metaTrack('CompleteRegistration', {
        content_name: planCode,
        status: plan && plan.price_cents > 0 ? 'paid_plan' : 'free_plan',
        value: planValue(plan?.price_cents),
        currency: planCurrency(plan?.currency),
      });

      if (result.checkout_url) {
        // Funnel step 3 of 4 — handing off to Stripe.
        track('app_checkout_started', {
          plan_code: planCode,
          price_cents: plan?.price_cents,
        });
        // Fired before the redirect hands the tab to Stripe. fbq() sends over a
        // beacon, so this survives the navigation.
        metaTrack('InitiateCheckout', {
          content_name: planCode,
          num_items: 1,
          value: planValue(plan?.price_cents),
          currency: planCurrency(plan?.currency),
        });
        window.location.href = result.checkout_url;
      } else {
        onComplete(result.user, result.workspace ?? null);
      }
    } catch (err) {
      track('app_signup_failed', {
        plan_code: planCode,
        reason: err instanceof Error ? err.message : 'unknown',
      });
      setSubmitError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      );
      setSubmitting(false);
    }
  };

  const planPrice = plan
    ? plan.price_cents === 0
      ? 'Free'
      : `$${(plan.price_cents / 100).toFixed(0)}/${plan.billing_interval === 'year' ? 'yr' : 'mo'}`
    : null;

  // Only the north star surfaces. The other four sections were competing with
  // the grid for attention and losing — the profile is the argument, the
  // strategy document is reference the paid account can read at leisure.
  const northStar =
    preview?.strategy.sections.find((sec) => sec.key === 'north_star') ?? null;

  return (
    <div className={`oc-page${stage === 'reveal' ? ' oc-page--wide' : ''}`}>
      <div className="oc-bg" aria-hidden="true" />

      <div className="oc-inner">
        {/* ── Header ── */}
        <div className="oc-top">
          <div className="oc-brand">
            <ConistraLogo size={28} />
            <span className="oc-brand__name">Conistra</span>
          </div>
          {plan && planPrice && (
            <div className="oc-plan-badge">
              <span className="oc-plan-badge__name">{plan.name}</span>
              <span className="oc-plan-badge__sep">·</span>
              <span className="oc-plan-badge__price">{planPrice}</span>
            </div>
          )}
        </div>

        {/* ── Stage 1: Chat card ── */}
        {stage === 'chat' && (
          <div className="oc-card oc-card--chat oc-anim-enter">
            {/* Chat messages */}
            <div className="oc-chat-area">
              {messages.length === 0 && loading && (
                <div className="oc-msg oc-msg--assistant">
                  <div className="oc-avatar">
                    <ConistraLogo size={24} />
                  </div>
                  <div className="oc-bubble oc-bubble--assistant oc-bubble--typing">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <React.Fragment key={i}>
                  <div className={`oc-msg oc-msg--${msg.role} oc-msg-enter`}>
                    {msg.role === 'assistant' && (
                      <div className="oc-avatar">
                        <ConistraLogo size={24} />
                      </div>
                    )}
                    <div className={`oc-bubble oc-bubble--${msg.role}`}>{msg.content}</div>
                  </div>
                  {/* Quick-reply chips after the first greeting */}
                  {i === 0 && showQuickReplies && (
                    <div className="oc-quickreplies oc-msg-enter">
                      {QUICK_REPLIES.map((reply) => (
                        <button
                          key={reply}
                          className="oc-chip"
                          onClick={() => handleQuickReply(reply)}
                          disabled={loading}
                        >
                          {reply}
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}

              {/* The agent's own chips for the turn it just took. */}
              {!loading && quickReplies.length > 0 && (
                <div className="oc-quickreplies oc-msg-enter">
                  {quickReplies.map((reply) => (
                    <button
                      key={reply}
                      className="oc-chip"
                      onClick={() => handleQuickReply(reply)}
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}

              {/* Handoff. Replaces the chips: at this point there is exactly
                  one thing to do, and it should feel like theirs to do. */}
              {!loading && handoffLabel && (
                <div className="oc-handoff oc-msg-enter">
                  <button
                    type="button"
                    className="oc-handoff__btn"
                    onClick={() => {
                      if (!context) return;
                      setStage('building');
                      runPreview(context);
                    }}
                  >
                    <span>{handoffLabel}</span>
                    <span className="oc-handoff__arrow" aria-hidden="true">→</span>
                  </button>
                </div>
              )}

              {/* Typing indicator */}
              {loading && messages.length > 0 && (
                <div className="oc-msg oc-msg--assistant">
                  <div className="oc-avatar">
                    <ConistraLogo size={24} />
                  </div>
                  <div className="oc-bubble oc-bubble--assistant oc-bubble--typing">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* Compose — hidden at handoff: there is nothing left to say, and
                an open input invites them to keep typing into a finished chat. */}
            <div className={`oc-compose${handoffLabel ? ' is-hidden' : ''}`}>
              <input
                ref={inputRef}
                className="oc-compose__input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder={loading ? 'Thinking…' : 'Type your answer…'}
                disabled={loading}
              />
              <button
                className="oc-compose__send"
                onClick={handleSend}
                disabled={loading || !input.trim()}
                aria-label="Send"
              >
                {loading ? <span className="oc-spinner" /> : '→'}
              </button>
            </div>
          </div>
        )}

        {/* ── Stage 2: Building — the promise being kept, in public ── */}
        {stage === 'building' && (
          <div className="oc-card oc-card--building oc-anim-slide-up">
            <div className="oc-build-head">
              <div className="oc-build-orb" aria-hidden="true">
                <ConistraLogo size={26} />
              </div>
              <h2 className="oc-build-title">Building your page</h2>
              <p className="oc-build-sub">
                {context?.niche ? `“${context.niche}”` : 'One moment…'}
              </p>
            </div>

            <ol className="oc-steps" aria-live="polite">
              {BUILD_STEPS.map((step, i) => {
                const state = i < buildStep ? 'done' : i === buildStep ? 'active' : 'todo';
                return (
                  <li key={step.key} className={`oc-step oc-step--${state}`}>
                    <span className="oc-step__dot" aria-hidden="true">
                      {state === 'done' ? '✓' : ''}
                    </span>
                    <span className="oc-step__label">{step.label}</span>
                  </li>
                );
              })}
            </ol>

            {/* Real output during the wait, not a spinner: the north star lands
                while the posts are still being written. */}
            {northStar && (
              <div className="oc-build-peek oc-anim-slide-up">
                <span className="oc-build-peek__label">Your north star</span>
                <p className="oc-build-peek__text">{northStar.body}</p>
              </div>
            )}

            {previewError && (
              <div className="oc-build-error" role="alert">
                <p>{previewError}</p>
                <button type="button" className="oc-build-retry" onClick={retryPreview}>
                  Try again
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Stage 3: Reveal — the page they are buying, as a page ── */}
        {stage === 'reveal' && preview && (
          <div className="oc-reveal oc-anim-slide-up">
            <div className="oc-reveal-head">
              {/* Their words up here, what it became right below. The contrast
                  is the point — "Your page" twice just says it twice. */}
              <span className="oc-reveal-head__label">You typed</span>
              <p className="oc-reveal-head__niche">{context?.niche}</p>
            </div>

            {/* A profile, not a list of cards. It is what the product actually
                sells, it fits three posts in one row instead of three screens,
                and the nine dim slots are the open loop the button closes. */}
            <div className="oc-phone">
              <div className="oc-phone__bar">
                {/* Their initial, not our logo. This is their page — putting
                    the Conistra mark in the avatar slot quietly says otherwise. */}
                <div className="oc-phone__avatar" aria-hidden="true">
                  {(preview.strategy.account_name || context?.niche || '?')
                    .trim()
                    .charAt(0)
                    .toUpperCase()}
                </div>
                <div className="oc-phone__id">
                  <span className="oc-phone__handle">
                    {preview.strategy.account_name || context?.niche}
                  </span>
                  <span className="oc-phone__meta">{preview.strategy.posting_cadence}</span>
                </div>
              </div>

              {preview.strategy.bio && (
                <p className="oc-phone__bio">{preview.strategy.bio}</p>
              )}

              <div className="oc-grid">
                {preview.posts.slice(0, revealedPosts).map((post, i) => (
                  <GridTile
                    key={`${post.title}-${i}`}
                    post={post}
                    index={i}
                    active={openPost === i}
                    onOpen={() => setOpenPost(openPost === i ? null : i)}
                    onGenerate={() => goToAccount('tile')}
                  />
                ))}
                {Array.from({ length: GRID_SLOTS - revealedPosts }).map((_, i) => (
                  <div key={`empty-${i}`} className="oc-tile oc-tile--empty" aria-hidden="true" />
                ))}
              </div>
            </div>

            <p className="oc-grid-note">
              {revealedPosts < preview.posts.length
                ? 'Writing your posts…'
                : 'Tap a post to read it. The rest fill themselves in, every week.'}
            </p>

            {/* Detail on demand — the grid stays the overview. */}
            {openPost !== null && preview.posts[openPost] && (
              <div className="oc-detail oc-anim-slide-up">
                <div className="oc-detail__meta">
                  <span className="oc-post__format">
                    {preview.posts[openPost].content_format === 'carousel'
                      ? 'Carousel'
                      : 'Photo'}
                  </span>
                  <button
                    type="button"
                    className="oc-detail__close"
                    onClick={() => setOpenPost(null)}
                    aria-label="Close post"
                  >
                    ×
                  </button>
                </div>
                <h3 className="oc-detail__title">{preview.posts[openPost].title}</h3>
                <p className="oc-detail__caption">{preview.posts[openPost].caption}</p>
                <div className="oc-detail__brief">
                  <span className="oc-detail__brief-label">Image brief</span>
                  <p>{preview.posts[openPost].image_brief}</p>
                </div>
                <button
                  type="button"
                  className="oc-detail__gen"
                  onClick={() => goToAccount('detail')}
                >
                  Generate this image →
                </button>
              </div>
            )}

            {northStar && (
              <div className="oc-northstar">
                <span className="oc-northstar__label">North star</span>
                <p className="oc-northstar__text">{northStar.body}</p>
              </div>
            )}

            <div className="oc-cta-dock">
              <button type="button" className="oc-cta" onClick={() => goToAccount('dock')}>
                <span>Generate my {preview.posts.length} images</span>
                <span className="oc-cta__arrow" aria-hidden="true">→</span>
              </button>
              <p className="oc-cta-note">
                {plan && planPrice ? `${plan.name} · ${planPrice} · cancel anytime` : 'Cancel anytime'}
              </p>
            </div>
          </div>
        )}

        {/* ── Stage 4: Signup — one question at a time ── */}
        {stage === 'account' && (
          <div className="oc-signup oc-anim-slide-up">
            {/* Thin, honest progress. Three segments, because there are three
                questions and the end has to look close from the first one. */}
            <div className="oc-signup__rail" aria-hidden="true">
              {SIGNUP_STEPS.map((step, i) => (
                <span
                  key={step.key}
                  className={`oc-signup__seg${i <= signupStep ? ' is-done' : ''}`}
                />
              ))}
            </div>

            <button
              type="button"
              className="oc-signup__back"
              onClick={() => (signupStep === 0 ? setStage('reveal') : setSignupStep((n) => n - 1))}
              disabled={submitting}
            >
              ← {signupStep === 0 ? 'Back to my page' : 'Back'}
            </button>

            {SIGNUP_STEPS.map((step, i) =>
              i !== signupStep ? null : (
                <div key={step.key} className="oc-signup__step oc-signup-step-enter">
                  <h2 className="oc-signup__q">
                    {step.key === 'first_name' && 'What should we call you?'}
                    {step.key === 'email' &&
                      `Nice to meet you${accountForm.first_name ? `, ${accountForm.first_name.trim()}` : ''}. What's your email?`}
                    {step.key === 'password' && 'Pick a password.'}
                  </h2>
                  <p className="oc-signup__hint">
                    {step.key === 'first_name' && 'So your page sounds like a person, not a brand deck.'}
                    {step.key === 'email' && 'This is how you get back in. Nothing else.'}
                    {step.key === 'password' && 'Eight characters is plenty.'}
                  </p>

                  <input
                    key={`input-${step.key}`}
                    className="oc-signup__input"
                    type={step.type}
                    value={accountForm[step.key]}
                    autoComplete={step.autoComplete}
                    placeholder={step.placeholder}
                    autoFocus
                    disabled={submitting}
                    onChange={(e) => {
                      const v = e.target.value;
                      setAccountForm((p) => ({ ...p, [step.key]: v }));
                      if (accountErrors[step.key])
                        setAccountErrors((p) => ({ ...p, [step.key]: undefined }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        advance();
                      }
                    }}
                  />

                  {accountErrors[step.key] && (
                    <p className="oc-signup__err" role="alert">
                      {accountErrors[step.key]}
                    </p>
                  )}
                </div>
              )
            )}

            {submitError && (
              <div className="oc-signup__fail" role="alert">
                {submitError}
              </div>
            )}

            <button
              type="button"
              className="oc-signup__next"
              onClick={advance}
              disabled={submitting || !accountForm[SIGNUP_STEPS[signupStep].key].trim()}
            >
              {submitting ? (
                <>
                  <span className="oc-spinner oc-spinner--dark" />
                  <span>Setting up…</span>
                </>
              ) : (
                <>
                  <span>
                    {signupStep < SIGNUP_STEPS.length - 1
                      ? 'Continue'
                      : plan && plan.price_cents > 0
                        ? 'Go to checkout'
                        : 'Create my account'}
                  </span>
                  <span className="oc-signup__arrow" aria-hidden="true">→</span>
                </>
              )}
            </button>

            {signupStep === SIGNUP_STEPS.length - 1 && plan && plan.price_cents > 0 && (
              <p className="oc-account-stripe-note">Secure checkout via Stripe</p>
            )}
          </div>
        )}

        {/* Hidden on the reveal: it would sit under the docked CTA, and that
            screen is deliberately a one-action screen. */}
        {stage !== 'reveal' && stage !== 'account' && (
          <button type="button" className="oc-login-link" onClick={onBack}>
            Go to Login
          </button>
        )}
      </div>
    </div>
  );
}

export default OnboardingCollectorPage;
